import type { JSONContent } from '@tiptap/core';

import type { CommunityAnnouncementMentionType } from '../communityPostComposer/CommunityPostComposer.type';
import {
  type CommunityAnnouncementMentions,
  extractCommunityAnnouncementMentions,
} from './extractCommunityAnnouncementMentions';

export interface CommunityAnnouncementMentionReach {
  type: CommunityAnnouncementMentionType;
  id: string;
  name: string;
  member_count: number;
  recent_uses: number;
}

export interface CommunityAnnouncementConfirmation {
  member_count: number;
  announcement_mentions: CommunityAnnouncementMentionReach[];
}

export type CommunityAnnouncementConfirmationTranslate = (key: string, values?: Record<string, unknown>) => string;

/** What confirming does: post a new post, save an edit or schedule a post to publish later. */
export type CommunityAnnouncementConfirmationAction = 'post' | 'save' | 'schedule';

export interface CommunityAnnouncementConfirmationCopy {
  title: string;
  description: string;
  confirmLabel: string;
  // Warnings as a key and its values, so the app can render the name in bold.
  recentUses: { id: string; key: string; values: { name: string; count: number } }[];
}

const KEY = 'community.composer.announcement_confirmation';

export const communityAnnouncementConfirmationAction = ({
  isEditMode,
  isScheduling,
}: {
  isEditMode: boolean;
  isScheduling: boolean;
}): CommunityAnnouncementConfirmationAction => {
  if (isEditMode) return 'save';
  if (isScheduling) return 'schedule';
  return 'post';
};

// Null right away for content without @everyone or @badge, so its post goes out without waiting. Otherwise resolves
// to the confirmation to show, or to null when the author can't notify anyone (`fetchConfirmation` resolves null on
// the endpoint's 403) or none of the announcement mentions is new.
export const fetchCommunityAnnouncementConfirmation = (
  content: JSONContent,
  fetchConfirmation: (mentions: CommunityAnnouncementMentions) => Promise<CommunityAnnouncementConfirmation | null>,
): Promise<CommunityAnnouncementConfirmation | null> | null => {
  const mentions = extractCommunityAnnouncementMentions(content);
  if (!mentions.everyone && mentions.badgeIds.length === 0) return null;

  return fetchConfirmation(mentions).then((confirmation) =>
    confirmation?.announcement_mentions.length ? confirmation : null,
  );
};

export const communityAnnouncementConfirmationCopy = ({
  confirmation: { member_count: memberCount, announcement_mentions: mentions },
  channelName,
  confirmAction,
  translate,
}: {
  confirmation: CommunityAnnouncementConfirmation;
  channelName: string | null;
  confirmAction: CommunityAnnouncementConfirmationAction;
  translate: CommunityAnnouncementConfirmationTranslate;
}): CommunityAnnouncementConfirmationCopy => {
  const isScheduling = confirmAction === 'schedule';
  const mentionsEveryone = mentions.some((mention) => mention.type === 'everyone');
  const badges = mentions.filter((mention) => mention.type === 'badge').map((mention) => mention.name);

  const reachDescription = () => {
    if (!mentionsEveryone) return translate(`${KEY}.description_badges`, { badges });
    if (channelName) return translate(`${KEY}.description_everyone_in_channel`, { channel: channelName });
    return translate(`${KEY}.description_everyone`);
  };

  const titleAndDescription = () => {
    // Some mention reaches members, so a zero total means they were all notified about this post before.
    if (memberCount === 0 && mentions.some((mention) => mention.member_count > 0)) {
      return { title: translate(`${KEY}.nobody_new_title`), description: translate(`${KEY}.already_notified`) };
    }
    if (memberCount === 0) {
      return {
        title: translate(`${KEY}.nobody_title`),
        description: mentionsEveryone
          ? translate(`${KEY}.nobody_can_see`)
          : translate(`${KEY}.nobody_has_badges`, { badges, count: badges.length }),
      };
    }

    const description = [
      reachDescription(),
      mentions.length > 1 && translate(`${KEY}.tagged_twice`),
      // A scheduled post notifies nobody yet; the count is who gets notified once it publishes.
      isScheduling && translate(`${KEY}.notified_on_publish`),
    ];
    return {
      title: translate(`${KEY}.title`, { count: memberCount }),
      description: description.filter(Boolean).join(' '),
    };
  };

  return {
    ...titleAndDescription(),
    confirmLabel: translate(`${KEY}.${confirmAction}_${memberCount > 0 ? 'and_notify' : 'anyway'}`),
    recentUses: mentions
      .filter((mention) => mention.recent_uses > 0)
      .map((mention) => ({
        id: mention.id,
        // The recent uses count published posts only, which a scheduled post isn't yet.
        key: isScheduling ? `${KEY}.recent_published_use` : `${KEY}.recent_use`,
        values: { name: mention.name, count: mention.recent_uses },
      })),
  };
};
